import React, { useState } from 'react';
import {
  ChevronDown,
  Coins,
  HelpCircle,
  Info,
  MessageCircle,
  Phone,
  UserCheck,
} from 'lucide-react';

export const HelpCenterPage: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const handleQuickHelp = () => {
    const whatsappMessage = "Hi! I need quick help with the app.";
    const encodedMessage = encodeURIComponent(whatsappMessage);
    window.open(`https://wa.me/255614021404?text=${encodedMessage}`, '_blank');
  };

  const faqs = [
    {
      category: 'About the System',
      icon: Info,
      items: [
        {
          q: 'What is the MAIPT platform?',
          a: 'MAIPT is your all-in-one platform for the Industrial Practical Training (PT) program. It helps you create and manage your daily reports, weekly reports (logbook), and general report, export your reports, and keep track of your tokens.'
        },
        {
          q: 'How do I use the platform?',
          a: 'After logging in, start from the Home dashboard. Write your daily and weekly reports as you complete your training, submit your weekly reports to build the logbook, then prepare the general report. Use the PT Assessment Guide to understand what examiners look for, and use tokens from the Billing page when you need to export reports.'
        },
        {
          q: 'Do reports work offline?',
          a: 'Yes. You can create daily and weekly reports while offline, and they will sync when your internet connection is restored.'
        },
        {
          q: 'What are tokens?',
          a: 'Tokens are the in-app currency used for exporting your reports. Your remaining tokens are always shown in the sidebar and header. When you run out, add more from the Billing page.'
        },
      ]
    },
    {
      category: 'PT Assessment Guide',
      icon: UserCheck,
      items: [
        {
          q: 'How is my PT work assessed?',
          a: 'Your PT assessment is built on two parts. The first part covers your logbook, your company training instructor assessment, and your supervisor assessment, and together these make up 40% of the PT. How that 40% is shared between the three is set by your department, so it is not the same for every department. The second part is your General Report, which makes up 60% and is the same for all departments.'
        },
        {
          q: 'What does the company training instructor assessment cover?',
          a: 'The training instructor evaluates your performance throughout the whole PT period. This is based on your daily activities, learning progress, technical contribution, conduct, punctuality, attendance, and adherence to company procedures.'
        },
        {
          q: 'What does the supervisor assessment cover?',
          a: 'Your university supervisor usually visits at least once, often from around week 5 onwards. They may review your weekly reports and your progress at the workplace. The focus is on outcomes, training quality, and how engaged you are.'
        },
        {
          q: 'What makes a good logbook?',
          a: 'Your logbook is made up of your weekly reports. It should show your weekly work and daily activities clearly, explain your main job with detailed steps and supporting diagrams where needed, and demonstrate technical writing, consistency, and learning growth.'
        },
        {
          q: 'What should the General Report contain?',
          a: 'The General Report makes up 60% of the PT assessment and is the same for all departments. It has two key parts: The Company and The Process. It must be signed by you and countersigned by the training instructor. Include technical explanation, diagrams, analysis, and practical recommendations. The balance between the company and process parts changes depending on whether you are doing PT1, PT2, or PT3.'
        },
        {
          q: 'When are reports submitted and what are the penalties?',
          a: 'All reports should be submitted by the second week Friday after the university reopens. Each late submission item attracts a penalty, which applies to late arrival notes, late general report submission, and late logbook submission.'
        },
        {
          q: 'How do PT1, PT2 and PT3 differ?',
          a: 'PT1 needs a stronger company section with basic process analysis, PT2 requires a balanced company and process focus, and PT3 is process-heavy with stronger engineering analysis. Higher PT levels require deeper analysis and stronger justification. Use proper technical language, clear diagrams, and practical recommendations.'
        },
      ]
    },
    {
      category: 'Billing & Support',
      icon: Coins,
      items: [
        {
          q: 'Why do I need to donate?',
          a: 'Donating helps sustain the platform. MAIPT is not run for profit — by donating you support the system so it can continue to provide help and services for students. Your support keeps the platform running for the benefit of students.'
        },
        {
          q: 'My donation/payment is not showing as confirmed. What should I do?',
          a: 'Sometimes a transaction can be delayed and not show as confirmed immediately. If you have donated and your transaction has not been confirmed yet, do not worry — contact the MAIPT team right away using the contact details provided on this page for immediate help, and they will resolve it for you.'
        },
        {
          q: 'How do I submit my payment for confirmation?',
          a: 'Go to the Billing page and submit your transaction receipt as instructed. Once submitted, the MAIPT team will confirm it. If it is taking longer than expected, reach out through the contacts below for immediate help.'
        },
        {
          q: 'Data not syncing?',
          a: 'Check your internet connection and try refreshing the page. If the problem continues, contact the MAIPT team for support.'
        },
      ]
    }
  ];

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-orange-600 dark:text-orange-400">Help Center</h1>

      {/* Quick Help Button */}
      <div className="card space-y-3">
        <h2 className="font-semibold text-lg text-gray-800 dark:text-white">Need Immediate Help?</h2>
        <p className="text-gray-700 dark:text-gray-300 text-sm">Get instant support via WhatsApp or call us directly</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            onClick={handleQuickHelp}
            className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <MessageCircle className="w-5 h-5" />
            <span>Chat with Support</span>
          </button>
          <a
            href="tel:+255614021404"
            className="w-full bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <Phone className="w-5 h-5" />
            <span>Call Support</span>
          </a>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="space-y-5">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-lg text-gray-800 dark:text-white">
            <HelpCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            Frequently Asked Questions
          </h2>
          <p className="text-gray-700 dark:text-gray-300 text-sm mt-1">
            Everything you need to know about the platform, the PT assessment, and getting support.
          </p>
        </div>

        {faqs.map((group) => {
          const GroupIcon = group.icon;
          return (
            <div key={group.category} className="card space-y-3">
              <h3 className="flex items-center gap-2 font-semibold text-base text-gray-800 dark:text-white">
                <GroupIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                {group.category}
              </h3>
              <div className="space-y-2">
                {group.items.map((faq) => {
                  const isOpen = openFaq === faq.q;
                  return (
                    <div
                      key={faq.q}
                      className="rounded-2xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : faq.q)}
                        aria-expanded={isOpen}
                        className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-gray-800 dark:text-gray-100 select-none"
                      >
                        {faq.q}
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {isOpen && (
                        <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
                          {faq.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Contact Section */}
      <div className="card space-y-3">
        <h2 className="flex items-center gap-2 font-semibold text-lg text-gray-800 dark:text-white">
          <Phone className="h-5 w-5 text-orange-600 dark:text-orange-400" />
          Other Contact Methods
        </h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-gray-700 dark:text-gray-300">Email Support</span>
            <a
              className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 underline transition-colors"
              href="mailto:kodinsoftwares@gmail.com"
            >
              kodinsoftwares@gmail.com
            </a>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-700 dark:text-gray-300">WhatsApp</span>
            <a
              className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 underline transition-colors"
              href="https://wa.me/255614021404"
              target="_blank"
              rel="noreferrer"
            >
              +255 614 021 404
            </a>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-700 dark:text-gray-300">Phone</span>
            <a
              className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 underline transition-colors"
              href="tel:+255614021404"
            >
              +255 614 021 404
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenterPage;